import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Form,
  FormGroup,
  TextInput,
  TextArea,
  NumberInput,
  Checkbox,
  DatePicker,
  DatePickerInput,
  Dropdown,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  InlineNotification,
  Tag,
  Link,
  UnorderedList,
  ListItem,
} from '@carbon/react';
import { Add, Star, StarFilled, TrashCan, Image as ImageIcon } from '@carbon/react/icons';
import CreatableSelect from 'react-select/creatable';
import moment from 'moment';

import { SideForm } from 'components/SideForms/SideForm';
import { hideForm } from 'services/forms';
import { useGetTagsQuery } from 'services/tags';
import { showToast } from '@/redux/new_services/toasts';
import { SelectableGroups } from '@/components/GroupsSelection/Groups';
import useUser from '@/lib/hooks/useUser';

import {
  useGetVariantGroupQuery,
  useDeleteVariantGroupMutation,
  useAddVariantMutation,
  useUpdateVariantMutation,
  useRemoveVariantMutation,
  useSetVariantGroupDefaultMutation,
  useSyncVariantGroupSettingsMutation,
} from '@/redux/new_services/variantGroups';
import VariantSlotPicker from './VariantSlotPicker';
import MediaPreview, { previewSrc } from './MediaPreview';

// react-select uses inline styles to blend with Carbon — match the original
// element form so admins keep a consistent look between Element and Group.
const reactSelectStyles = {
  control: (provided) => ({
    ...provided,
    borderRadius: 0,
    border: 0,
    backgroundColor: '#f4f4f4',
    borderBottom: '1px solid var(--cds-border-strong)',
  }),
};

// Pick the media that represents the "settings template" of the group —
// that's what we read shared element-settings from on first load. Default
// variant's landscape comes first; portrait + first-variant fallbacks
// follow the same logic as the gallery preview.
function pickTemplateMedia(group) {
  const variants = group?.variants || [];
  if (variants.length === 0) return null;
  const def =
    variants.find((v) => v.model.ID === group.default_variant_id) || variants[0];
  return def?.landscape_media || def?.portrait_media || null;
}

// Empty default for the shared-settings state, mirroring the initial state
// of ElementForm so a freshly opened group doesn't show stale values.
const blankSettings = () => ({
  default_duration: 10,
  public: true,
  remove_after: false,
  remove_date: '',
  highlight: false,
  highlight_start: '',
  highlight_end: '',
  tags: [],
  groups: [],
});

// VariantGroupForm — full-fat side form for editing a variant group. It
// mirrors ElementForm's tab layout (Allgemein / Erweitert / Verwendung)
// plus a fourth Varianten-tab. The "shared" element-settings (categorisation,
// group access, advanced scheduling) live on the underlying media but are
// edited centrally here and synced to every member through one backend call.
const VariantGroupForm = ({ id }) => {
  const dispatch = useDispatch();
  const { isAdmin } = useUser();

  const { data: group, isFetching, refetch } = useGetVariantGroupQuery(id, { skip: !id });

  const [syncSettings, { isLoading: isSyncing }] = useSyncVariantGroupSettingsMutation();
  const [deleteGroup] = useDeleteVariantGroupMutation();
  const [addVariant, { isLoading: isAdding }] = useAddVariantMutation();
  const [updateVariant] = useUpdateVariantMutation();
  const [removeVariant] = useRemoveVariantMutation();
  const [setDefault] = useSetVariantGroupDefaultMutation();

  const { data: tagOptions } = useGetTagsQuery();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [settings, setSettings] = useState(blankSettings());
  const [unsaved, setUnsaved] = useState(false);
  const [error, setError] = useState(null);
  const [newVariantName, setNewVariantName] = useState('');
  const [previewVariantId, setPreviewVariantId] = useState(null);

  const today = moment();

  // Re-seed local state from the freshly fetched group + its template
  // media. The dependency array intentionally only watches the group id +
  // updated_at so editing the local form doesn't trigger a re-seed.
  useEffect(() => {
    if (!group) return;
    setName(group.name || '');
    setDescription(group.description || '');
    const tpl = pickTemplateMedia(group);
    setSettings({
      default_duration: tpl?.default_duration || 10,
      public: tpl?.public ?? true,
      remove_after: tpl?.remove_after || false,
      remove_date: tpl?.remove_date && tpl.remove_date !== '0001-01-01T00:00:00Z' ? tpl.remove_date : '',
      highlight: tpl?.highlight || false,
      highlight_start: tpl?.highlight_start && tpl.highlight_start !== '0001-01-01T00:00:00Z' ? tpl.highlight_start : '',
      highlight_end: tpl?.highlight_end && tpl.highlight_end !== '0001-01-01T00:00:00Z' ? tpl.highlight_end : '',
      tags: tpl?.tags || [],
      groups: (tpl?.groups || []).map((g) => g.model?.ID).filter(Boolean),
    });
    setUnsaved(false);
  }, [group?.model?.ID, group?.model?.UpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const variants = useMemo(() => group?.variants || [], [group]);
  const defaultVariantId = group?.default_variant_id || null;
  const usageStreams = group?._streams || [];
  const usageCount = group?._stream_count || 0;

  const onClose = () => dispatch(hideForm());

  const updateSetting = (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
    setUnsaved(true);
  };

  const onSubmit = async () => {
    if (!unsaved) {
      onClose();
      return;
    }
    if (!name.trim()) {
      setError('Bitte einen Namen angeben');
      return;
    }
    try {
      await syncSettings({
        id,
        name: name.trim(),
        description,
        settings: {
          default_duration: settings.default_duration,
          public: settings.public,
          remove_after: settings.remove_after,
          remove_date: settings.remove_date && settings.remove_date !== '' ? moment(settings.remove_date).toISOString() : '0001-01-01T00:00:00Z',
          highlight: settings.highlight,
          highlight_start: settings.highlight_start && settings.highlight_start !== '' ? moment(settings.highlight_start).toISOString() : '0001-01-01T00:00:00Z',
          highlight_end: settings.highlight_end && settings.highlight_end !== '' ? moment(settings.highlight_end).toISOString() : '0001-01-01T00:00:00Z',
          tags: settings.tags,
          groups: settings.groups.map((gid) => ({ model: { ID: gid } })),
        },
      }).unwrap();
      setUnsaved(false);
      dispatch(showToast({
        kind: 'success',
        title: 'Gespeichert',
        subtitle: 'Gruppen-Einstellungen wurden auf alle Mitglieder übertragen.',
      }));
    } catch {
      setError('Speichern fehlgeschlagen');
    }
  };

  const handleAddVariant = async () => {
    const vname = newVariantName.trim();
    if (!vname || !group) return;
    try {
      await addVariant({ id: group.model.ID, name: vname }).unwrap();
      setNewVariantName('');
      refetch();
    } catch {
      setError('Variante konnte nicht hinzugefügt werden');
    }
  };

  const handleRenameVariant = async (variant, value) => {
    const next = value.trim();
    if (next === variant.name) return;
    try {
      await updateVariant({
        id: variant.model.ID,
        groupId: group.model.ID,
        name: next,
        position: variant.position,
        landscapeMediaId: variant.landscape_media_id,
        portraitMediaId: variant.portrait_media_id,
      }).unwrap();
    } catch {
      setError('Umbenennen fehlgeschlagen');
    }
  };

  const handleSlotChange = async (variant, slotKey, mediaId) => {
    try {
      await updateVariant({
        id: variant.model.ID,
        groupId: group.model.ID,
        name: variant.name,
        position: variant.position,
        landscapeMediaId: slotKey === 'landscape' ? mediaId : variant.landscape_media_id,
        portraitMediaId: slotKey === 'portrait' ? mediaId : variant.portrait_media_id,
      }).unwrap();
    } catch {
      setError('Medien-Zuordnung fehlgeschlagen');
    }
  };

  const handleRemoveVariant = async (variant) => {
    if (!confirm(`Variante "${variant.name}" wirklich löschen?`)) return;
    try {
      await removeVariant({ id: variant.model.ID, groupId: group.model.ID }).unwrap();
    } catch {
      setError('Variante konnte nicht gelöscht werden');
    }
  };

  const handleSetDefault = async (variant) => {
    try {
      await setDefault({ id: group.model.ID, variantId: variant.model.ID }).unwrap();
    } catch {
      setError('Default-Markierung fehlgeschlagen');
    }
  };

  const handleDeleteGroup = async () => {
    if (!confirm('Gesamte Varianten-Gruppe wirklich löschen? Alle Varianten und enthaltenen Medien werden soft-gelöscht.')) return;
    try {
      await deleteGroup(group.model.ID).unwrap();
      dispatch(showToast({
        kind: 'success',
        title: 'Gelöscht',
        subtitle: 'Varianten-Gruppe wurde entfernt.',
      }));
      onClose();
    } catch {
      setError('Löschen fehlgeschlagen');
    }
  };

  const handleTagsChange = (newValue, actionMeta) => {
    if (actionMeta.action === 'remove-value') {
      setSettings((s) => ({
        ...s,
        tags: s.tags.filter((t) => t.label !== actionMeta.removedValue.label),
      }));
    } else if (actionMeta.action === 'create-option' || actionMeta.action === 'select-option') {
      const opt = actionMeta.option;
      setSettings((s) => ({
        ...s,
        tags: [...s.tags, { label: opt.label, details: { ID: opt.value } }],
      }));
    }
    setUnsaved(true);
  };

  if (!id) return null;

  // Pair-aware preview at the top: shows the *currently previewed* variant's
  // landscape + portrait. Defaults to the group's marked default; the
  // operator can flip to other variants via the dropdown beneath the preview
  // without changing the actual default.
  const tpl = pickTemplateMedia(group);
  const previewVariant =
    variants.find((v) => v.model.ID === previewVariantId) ||
    variants.find((v) => v.model.ID === defaultVariantId) ||
    variants[0] ||
    null;
  const landscape = previewVariant?.landscape_media || null;
  const portrait = previewVariant?.portrait_media || null;

  const tabAllgemein = (
    <Form>
      <FormGroup legendText="">
        <TextInput
          id="variant-group-name"
          labelText="Name der Gruppe"
          value={name}
          onChange={(e) => { setName(e.target.value); setUnsaved(true); }}
          invalid={name.length < 2}
          invalidText="Bitte einen Namen angeben"
        />
        <TextArea
          id="variant-group-description"
          labelText="Beschreibung"
          value={description}
          onChange={(e) => { setDescription(e.target.value); setUnsaved(true); }}
          rows={2}
        />
      </FormGroup>
      <FormGroup legendText="Kategorisierung">
        <CreatableSelect
          isMulti
          noOptionsMessage={() => 'Wählen Sie'}
          formatCreateLabel={(i) => `"${i}" erstellen`}
          styles={reactSelectStyles}
          options={(tagOptions || []).map((t) => ({ label: t.label, value: t.details.ID }))}
          value={(settings.tags || []).map((t) => ({ label: t.label, value: t.details?.ID }))}
          onChange={handleTagsChange}
        />
      </FormGroup>
      {isAdmin && (
        <FormGroup legendText="Gruppenzuordnung" helperText="Beschränkt die Verfügbarkeit auf einzelne Gruppen">
          <SelectableGroups
            selection={settings.groups}
            onSelectionChange={(gids) => updateSetting('groups', gids)}
          />
        </FormGroup>
      )}
      {isAdmin && (
        <Checkbox
          labelText="Öffentlich sichtbar"
          id="variant-group-public"
          checked={settings.public}
          onChange={(_, { checked }) => updateSetting('public', checked)}
        />
      )}
    </Form>
  );

  const tabErweitert = (
    <Form>
      <FormGroup legendText="">
        <NumberInput
          id="variant-group-default-duration"
          label="Anzeigedauer in Sekunden"
          value={settings.default_duration}
          step={1}
          allowEmpty={false}
          onChange={(_e, { value }) => updateSetting('default_duration', Number(value) || 10)}
          invalid={settings.default_duration < 1 || settings.default_duration > 5 * 60}
          invalidText="Wert muss zwischen 1 und 300 liegen"
          helperText="Die Anzeigedauer wird auf alle Mitglieder-Medien angewendet"
        />
      </FormGroup>
      {isAdmin && (
        <FormGroup legendText="Automatisch entfernen" helperText="Entfernt alle Mitglied-Medien zum Stichtag">
          <Checkbox
            id="variant-group-remove-after"
            labelText="Automatisch löschen aktivieren"
            checked={settings.remove_after}
            onChange={(_, { checked }) => updateSetting('remove_after', checked)}
          />
          <DatePicker
            datePickerType="single"
            dateFormat="d.m.Y"
            minDate={today.format('DD.MM.YYYY')}
            locale="de"
            short
            onChange={(e) => updateSetting('remove_date', e[0]?.toLocaleDateString('en-CA') || '')}
            value={settings.remove_date ? [Date.parse(settings.remove_date)] : []}
          >
            <DatePickerInput
              id="variant-group-remove-date"
              labelText="Stichtag"
              hideLabel
              size="md"
              placeholder={today.format('DD.MM.YYYY')}
            />
          </DatePicker>
        </FormGroup>
      )}
      {isAdmin && (
        <FormGroup legendText="Hervorheben" helperText="Mitglieder werden im genannten Zeitraum hervorgehoben">
          <Checkbox
            id="variant-group-highlight"
            labelText="Hervorhebung aktivieren"
            checked={settings.highlight}
            onChange={(_, { checked }) => updateSetting('highlight', checked)}
          />
          <DatePicker
            datePickerType="range"
            dateFormat="d.m.Y"
            minDate={today.format('DD.MM.YYYY')}
            locale="de"
            short={false}
            onChange={(e) => {
              updateSetting('highlight_start', e[0]?.toLocaleDateString('en-CA') || '');
              updateSetting('highlight_end', e[1]?.toLocaleDateString('en-CA') || '');
            }}
            value={[
              settings.highlight_start ? Date.parse(settings.highlight_start) : null,
              settings.highlight_end ? Date.parse(settings.highlight_end) : null,
            ]}
          >
            <DatePickerInput id="variant-group-highlight-start" labelText="Start" hideLabel size="md" placeholder={today.format('DD.MM.YYYY')} />
            <DatePickerInput id="variant-group-highlight-end" labelText="Ende" hideLabel size="md" placeholder={today.format('DD.MM.YYYY')} />
          </DatePicker>
        </FormGroup>
      )}
    </Form>
  );

  const tabVerwendung = (
    <div className="status_tab">
      Diese Varianten-Gruppe wird in {usageCount > 0 ? usageCount : 'keinem'}{' '}
      {usageCount === 1 ? 'Stream' : 'Streams'} verwendet
      {usageCount > 0 ? ':' : '.'}
      <UnorderedList>
        {usageStreams.map((s) => (
          <ListItem key={s.model?.ID || s.ID || s.name}>{s.name}</ListItem>
        ))}
      </UnorderedList>
    </div>
  );

  const tabVarianten = (
    <section className="variant-group-form__variants">
      <header className="variant-group-form__variants-head">
        <ImageIcon size={20} />
        <h4>Varianten</h4>
        <span className="variant-group-form__count">{variants.length}</span>
      </header>

      {variants.length === 0 && (
        <p className="variant-group-form__hint">
          Keine Varianten vorhanden. Lege eine Variante an und ordne ihr
          Hoch- oder Querformat-Medien zu.
        </p>
      )}

      <ul className="variant-group-form__list">
        {variants.map((v) => {
          const isDefault = v.model.ID === defaultVariantId;
          return (
            <li key={v.model.ID} className={`variant-group-form__item ${isDefault ? 'is-default' : ''}`}>
              <div className="variant-group-form__item-head">
                <TextInput
                  id={`variant-name-${v.model.ID}`}
                  labelText="Variantname"
                  hideLabel
                  defaultValue={v.name}
                  onBlur={(e) => handleRenameVariant(v, e.target.value)}
                />
                {isDefault ? (
                  <Tag type="green" renderIcon={StarFilled}>Standard</Tag>
                ) : (
                  <Button kind="ghost" size="sm" renderIcon={Star} onClick={() => handleSetDefault(v)}>
                    Als Standard
                  </Button>
                )}
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={TrashCan}
                  iconDescription="Variante entfernen"
                  onClick={() => handleRemoveVariant(v)}
                />
              </div>
              <div className="variant-group-form__slots">
                <VariantSlotPicker
                  label="Querformat"
                  orientation={0}
                  media={v.landscape_media}
                  onSelect={(m) => handleSlotChange(v, 'landscape', m?.model?.ID || null)}
                  onClear={() => handleSlotChange(v, 'landscape', null)}
                />
                <VariantSlotPicker
                  label="Hochkant"
                  orientation={1}
                  media={v.portrait_media}
                  onSelect={(m) => handleSlotChange(v, 'portrait', m?.model?.ID || null)}
                  onClear={() => handleSlotChange(v, 'portrait', null)}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <div className="variant-group-form__add">
        <TextInput
          id="variant-new-name"
          labelText="Neue Variante"
          placeholder="z. B. 1 Euro"
          value={newVariantName}
          onChange={(e) => setNewVariantName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddVariant();
            }
          }}
        />
        <Button kind="secondary" renderIcon={Add} onClick={handleAddVariant} disabled={!newVariantName.trim() || isAdding}>
          Hinzufügen
        </Button>
      </div>
    </section>
  );

  return (
    <SideForm
      title="Varianten-Gruppe"
      subTitle={group?.name || 'Gruppe bearbeiten'}
      onClose={onClose}
      onSubmit={onSubmit}
      primaryButton={unsaved ? 'Speichern' : 'Schließen'}
      primaryButtonDisabled={isSyncing || isFetching}
      secondaryButton={unsaved ? 'Abbruch' : 'Verlassen'}
    >
      {error && (
        <InlineNotification
          kind="error"
          title="Fehler"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          lowContrast
        />
      )}

      {(landscape || portrait) && (
        <div className="variant-group-form__preview-wrap">
          <div className="element-form__pair-preview">
            <div className="element-form__pair-preview-slot element-form__pair-preview-slot--landscape">
              {previewSrc(landscape) ? (
                <MediaPreview media={landscape} fit="contain" />
              ) : (
                <div className="variant-group-form__preview-empty">Querformat fehlt</div>
              )}
            </div>
            <div className="element-form__pair-preview-slot element-form__pair-preview-slot--portrait">
              {previewSrc(portrait) ? (
                <MediaPreview media={portrait} fit="contain" />
              ) : (
                <div className="variant-group-form__preview-empty">Hochformat fehlt</div>
              )}
            </div>
          </div>
          {variants.length > 1 && (
            <div className="variant-group-form__preview-switcher">
              <Dropdown
                id="variant-group-form-preview-switcher"
                size="sm"
                titleText=""
                hideLabel
                label="Variante zur Vorschau wählen"
                items={variants.map((v) => ({ id: v.model.ID, label: v.name || `Variante ${v.model.ID}` }))}
                itemToString={(it) => it?.label || ''}
                selectedItem={
                  previewVariant
                    ? { id: previewVariant.model.ID, label: previewVariant.name || `Variante ${previewVariant.model.ID}` }
                    : null
                }
                onChange={(e) => setPreviewVariantId(e?.selectedItem?.id || null)}
              />
            </div>
          )}
        </div>
      )}

      <Tabs>
        <TabList aria-label="Varianten-Gruppen-Reiter" contained>
          <Tab id="vg-tab-allgemein">Allgemein</Tab>
          <Tab id="vg-tab-erweitert">Erweitert</Tab>
          <Tab id="vg-tab-varianten">Varianten</Tab>
          <Tab id="vg-tab-verwendung">Verwendung</Tab>
        </TabList>
        <TabPanels>
          <TabPanel className="tab-content">{tabAllgemein}</TabPanel>
          <TabPanel className="tab-content">{tabErweitert}</TabPanel>
          <TabPanel className="tab-content">{tabVarianten}</TabPanel>
          <TabPanel className="tab-content">{tabVerwendung}</TabPanel>
        </TabPanels>
      </Tabs>

      <hr />
      <Link href="#" onClick={(e) => { e.preventDefault(); handleDeleteGroup(); }}>
        Gesamte Gruppe löschen
      </Link>
      {tpl && (
        <p style={{ fontSize: '0.75rem', color: '#525252', marginTop: '0.5rem' }}>
          Einstellungen werden beim Speichern auf alle Mitglieder-Medien
          (Varianten und Hoch+Quer-Paare) übertragen.
        </p>
      )}
    </SideForm>
  );
};

export default VariantGroupForm;
